/**
 * 归因 capture 决策逻辑（纯函数，服务端 JS 版与客户端 TS 版规则一致）
 */
import { ATTRIBUTED_ENTRY_OVERWRITE_POLICY } from './constants';
import { isAttributedEntryPath, normalizePathnameWithoutLang } from './match-pathname';
import { isAttributedEntryReferrer } from './match-referrer';
import { hasUtmQueryParams } from './match-utm';

/** capture 决策的输入参数 */
export type AttributedEntryCaptureInput = {
  referrer: string;
  pathname: string;
  search?: string;
  existingEntryUrl?: string | null;
};

/**
 * 同一 session 内是否应覆盖已有 entry。
 * pathname（去 lang）或 query 与已有 attributed_entry_url 不同时返回 true。
 */
export const shouldReplaceExistingAttributedEntry = (
  existingEntryUrl: string,
  pathname: string,
  search = ''
): boolean => {
  try {
    const existing = new URL(existingEntryUrl);
    const existingPath = normalizePathnameWithoutLang(existing.pathname);
    const currentPath = normalizePathnameWithoutLang(pathname);
    if (existingPath !== currentPath) {
      return true;
    }
    return existing.search !== search;
  } catch {
    return true;
  }
};

/**
 * 当前请求是否应写入归因 cookie。
 * 调用方：服务端 tryCaptureAttributedEntry、客户端 captureAttributedEntryOnLanding。
 */
export const shouldCaptureAttributedEntry = (input: AttributedEntryCaptureInput): boolean => {
  if (!isAttributedEntryPath(input.pathname)) {
    return false;
  }

  const referrer = input.referrer.trim();
  const search = input.search ?? '';
  const referrerQualifies = referrer !== '' && isAttributedEntryReferrer(referrer);
  const utmOnlyQualifies = referrer === '' && hasUtmQueryParams(search);

  if (!referrerQualifies && !utmOnlyQualifies) {
    return false;
  }

  const existingEntryUrl = input.existingEntryUrl?.trim();
  if (existingEntryUrl) {
    if (!ATTRIBUTED_ENTRY_OVERWRITE_POLICY.allowOverwriteExisting) {
      return false;
    }
    if (!shouldReplaceExistingAttributedEntry(existingEntryUrl, input.pathname, input.search ?? '')) {
      return false;
    }
  }

  return true;
};

/**
 * 是否仅需校正 document_referrer_snapshot（不重新 capture entry）。
 * 场景：已有 attributed_entry_url，但 referrer 快照为空或与当前 document.referrer 不一致。
 * 仅客户端通道 B 使用；服务端无此分支。
 */
export const shouldReconcileReferrerSnapshot = (params: {
  clientReferrer: string;
  existingEntryUrl?: string | null;
  existingReferrerSnapshot?: string | null;
}): boolean => {
  const { clientReferrer, existingEntryUrl, existingReferrerSnapshot } = params;
  if (!existingEntryUrl?.trim()) {
    return false;
  }
  const trimmed = clientReferrer.trim();
  if (!trimmed || trimmed === (existingReferrerSnapshot ?? '').trim()) {
    return false;
  }
  return isAttributedEntryReferrer(trimmed);
};
